import {
  keyBy,
  omit,
  sortBy,
  values,
  groupBy,
  reduce,
  unionBy,
  includes,
} from 'lodash';
import { MARK_TDO_UPLOADING_END } from '@common-modules/engines/ingestion';
import { CREATE_TDO_SUCCESS } from '@redact-modules/uploads/actions';

import {
  CHANGE_FILTER,
  CLEAR_ALL,
  CLEAR_SELECTED_TDOS,
  CLEAR_SELECTED_TDOS_AND_TAG_DELETING,
  DELETE_ORGANIZATION_SDO_SUCCESS_UPDATE_STORE,
  FETCH_ORGANIZATION_SDOS,
  FETCH_ORGANIZATION_SDOS_FAILURE,
  FETCH_ORGANIZATION_SDOS_SUCCESS,
  FETCH_ORGANIZATION_TDO_BY_ID_SUCCESS,
  FETCH_ORGANIZATION_TDOS,
  FETCH_ORGANIZATION_TDOS_FAILURE,
  FETCH_ORGANIZATION_TDOS_SUCCESS,
  TOGGLE_SELECT_ALL_TDOS,
  TOGGLE_SELECT_TDO,
  UPDATE_PAGINATION,
  UPSERT_ORGANIZATION_SDO,
  UPSERT_ORGANIZATION_SDO_FAILURE,
  UPSERT_ORGANIZATION_SDO_SUCCESS,
  UPDATE_ORG_TDOS_TASKS,
  UPDATE_STORE_SOFT_DELETED_TDOS,
  MARK_ORG_TDO_NEW,
  MARK_ORG_TDO_RECORDED,
  MARK_ORG_TDO_SEEN,
  MARK_ORG_TDO_LAST_OPENED,
  MARK_HOME_LIST_STALE,
  CLEAR_HOME_LIST_STALE,
} from '../actions';
import {
  MainPageStore,
  StructuredDataObject,
  SummaryTDO,
  SummaryTDOTask,
  isSummaryTDORecord,
} from '../models';
import { defaultState } from '../store';
import { TDOId } from '@common-modules/universal/models/Brands';
import { FetchOrganizationTasksResponse } from '../services';
import { CaseReducer, createReducer } from '@reduxjs/toolkit';
import { sortFolderPath } from '../../helper';

type Re<P = unknown> = CaseReducer<MainPageStore, { payload: P; type: string }>;

const updateSoftDeletedtdos: Re<{ readonly tdoIds: TDOId[] }> = (
  state,
  { payload }
) => ({
  ...state,
  tdos: omit(state.tdos, payload.tdoIds),
});
const upsertFetchAll: Re = (state) => ({
  ...state,
  fetchingOrganizationTdos: true,
});

const upsertFetchFailureAll: (e: string) => Re = (error) => (state) => ({
  ...state,
  fetchingOrganizationTdos: false,
  error,
});

const upsertOrgSDOSuccess: Re<{
  readonly createStructuredData: StructuredDataObject;
}> = (state, { payload }) => ({
  ...state,
  sdos: keyBy(
    sortBy(
      values({
        ...state.sdos,
        [payload.createStructuredData.data.tdoId]: payload.createStructuredData,
      }),
      (sdo) => sdo.modifiedDateTime
    ), // .slice(1), // the slice removes oldest modified sdo - instead keep so we can check if hasMore pages
    (sdo) => sdo.data?.tdoId
  ),
  fetchingOrganizationTdos: false,
});

const deleteOrgSDOSuccess: Re<{ readonly tdoIds: string[] }> = (
  state,
  { payload }
) => ({
  ...state,
  sdos: omit(state.sdos, payload.tdoIds),
  tdos: (Object.keys(state.sdos) as Array<keyof typeof state.sdos>).reduce<{
    [key: string]: SummaryTDO;
  }>((result, tdoId) => {
    const tdo = state.tdos[tdoId];
    // VE-29447: an SDO can exist with its TDO not loaded yet — every upload
    // adds the SDO first, and pagination holds more SDOs than fetched TDOs.
    // Without this guard `omit(undefined, ...)` yields `{}`, which lands in
    // `tdos` as a phantom record: no `modifiedDateTime` ("Invalid Date"), no
    // `status` (so never `recorded` — renders the "Processing New Media"
    // pill), and no `isDeleting` to filter it out of the grid.
    if (tdo && !includes(payload.tdoIds, tdoId)) {
      result[tdoId] = omit(tdo, ['isDeleting']);
    }
    return result;
  }, {}),
  fetchingOrganizationTdos: false,
});

const fetchOrgSDOsSuccess: Re<{
  readonly structuredDataObjects: {
    readonly records: ReadonlyArray<StructuredDataObject>;
  };
}> = (state, { payload }) => ({
  ...state,
  fetchingOrganizationTdos: false,
  sdos: {
    // if loading single page will append/merge new sdos with existing - otherwise completely replace sdos
    ...(state.loadingNextPage && state.sdos),
    ...keyBy(
      (payload?.structuredDataObjects?.records || []).filter(
        (r) => !!r.data?.tdoId
      ),
      (o) => o.data?.tdoId
    ),
  },
});

const onUpdateOrgTdosTasks: Re<{
  tasks: FetchOrganizationTasksResponse['tasks']['records'];
}> = (state, { payload }) => {
  const joinTDOTaskList = (
    tdo: SummaryTDO,
    newTDOTasks: FetchOrganizationTasksResponse['tasks']['records']
  ): SummaryTDO => ({
    ...tdo,
    tasks: {
      ...tdo.tasks,
      records: unionBy(
        newTDOTasks,
        tdo?.tasks?.records || [],
        (task) => task.id
      ),
    },
  });

  const { tasks } = payload;

  // ignore tasks for TDOs that we're not tracking
  const filteredTasks = (tasks || []).filter(
    ({ targetId }) => state.tdos[targetId]
  );

  const TDOIdToTaskListMap = groupBy(
    filteredTasks,
    ({ targetId }: SummaryTDOTask) => targetId
  );

  const updatedTDOs = reduce(
    TDOIdToTaskListMap,
    (updatedTDOsMap, taskList, tdoId) => {
      const tdo = state.tdos[tdoId];
      if (!tdo) {
        return updatedTDOsMap;
      }
      return {
        ...updatedTDOsMap,
        [tdoId]: joinTDOTaskList(tdo, taskList),
      };
    },
    {}
  );

  return {
    ...state,
    tdos: {
      ...state.tdos,
      ...updatedTDOs,
    },
  };
};

const fetchOrgTDOsSuccess: Re<Record<string, SummaryTDO>> = (
  state,
  { payload }
) => ({
  ...state,
  fetchingOrganizationTdos: false,
  loaded: true,
  loadingNextPage: false,
  error: undefined,
  tdos: tdosSmartMerge(state.tdos, payload, state.sdos, state.loadingNextPage),
});

const tdosSmartMerge = (
  tdos: Record<string, SummaryTDO>,
  fresh: Record<string, SummaryTDO>,
  sdos: Record<string, StructuredDataObject>,
  loadingNextPage: boolean
) => {
  let newTdos = { ...tdos };
  // VE-26782: the `mapTDOs` response can carry a server-injected `warnings`
  // array next to the `id<tdoId>` aliases (query-cost guard, errors disabled).
  // Without this filter it is spread into a TDO-shaped object, keyed under
  // `undefined`, and rendered as a phantom tile.
  const freshArr = Object.values(fresh).filter(isSummaryTDORecord);
  freshArr.forEach((f) => {
    // `thumbnailUrl` is a signed URL that is re-signed (different string) on
    // every fetch. Swapping it in on each merge/poll forces the <img> to
    // re-download and makes the grid thumbnails flicker (VE-24916). Keep the
    // existing URL for TDOs that haven't actually changed so the src stays
    // stable; modified TDOs still pick up the fresh URL.
    const existing = tdos[f.id];
    const thumbnailUrl =
      existing?.thumbnailUrl && existing.modifiedDateTime === f.modifiedDateTime
        ? existing.thumbnailUrl
        : f.thumbnailUrl;
    newTdos[f.id] = {
      ...f,
      thumbnailUrl,
      folders: f.folders?.map((folder) => ({
        ...folder,
        folderPath: sortFolderPath(folder.folderPath),
      })),
    };
    const tdo = newTdos[f.id];
    if (tdo && f.tasks) {
      newTdos[f.id] = { ...tdo, tasks: f.tasks };
    }
  });
  const newArr = freshArr.filter((f) => f.id && f.id in tdos === false);
  if (newArr.length > 0) {
    newTdos = { ...newTdos, ...keyBy(newArr, (o) => o.id) };
  }
  if (!loadingNextPage) {
    const oldArr = Object.keys(tdos).filter(
      (tdo) => !Object.keys(sdos).includes(tdo)
    );
    if (oldArr.length > 0) {
      newTdos = omit(newTdos, oldArr);
    }
  }
  const updates: SummaryTDO[] = freshArr
    .filter((f) => {
      const tdo = newTdos[f.id];
      return tdo && tdo.modifiedDateTime !== f.modifiedDateTime;
    })
    .map((f) => {
      const tdo = newTdos[f.id];
      if (tdo?.primaryAsset) {
        return {
          ...f,
          primaryAsset: tdo.primaryAsset,
        };
      }
      return f;
    });
  if (updates.length > 0) {
    newTdos = { ...newTdos, ...keyBy(updates, (o) => o.id) };
  }

  return newTdos;
};

const fetchOrgTDODetailSuccess: Re<{ temporalDataObject: SummaryTDO }> = (
  state,
  { payload }
) => {
  const { temporalDataObject } = payload;
  const id = temporalDataObject.id;
  // VE-29447: this fetch can be kicked off (e.g. right after upload) and then
  // resolve after the file was moved to trash, re-adding the tile the trash
  // action just removed. The trash flow stamps `movedToTrashDateTime` on the
  // first details tag, so seeing it here means trash won the race.
  //
  // Deliberately the tag and NOT folder membership (`isTdoInTrash`, the usual
  // way this app asks "is it trashed?"): the stamp is written *before* the
  // folder move, which is a separate, later mutation. Inside this race window
  // the folder still reads as not-trashed, so switching to a folder check
  // reintroduces the bug — and, because the poll can then see
  // tag-set/folder-not-yet, surfaces the file as a half-built tile.
  const firstTag = temporalDataObject.details?.tags?.[0];
  const movedToTrashDateTime =
    firstTag &&
    typeof firstTag === 'object' &&
    'movedToTrashDateTime' in firstTag
      ? firstTag.movedToTrashDateTime
      : undefined;
  if (movedToTrashDateTime) {
    return state;
  }
  const base: SummaryTDO = {
    ...state.tdos[id],
    ...temporalDataObject,
  };
  return {
    ...state,
    tdos: {
      ...state.tdos,
      [id]: base,
    },
  };
};

/**
 * Patch a TDO's status to `recorded` after the PDF fast path wrote it
 * server-side. No-op when the TDO is absent or already recorded.
 */
const markOrgTdoRecorded: Re<{ tdoId: string }> = (
  state,
  { payload: { tdoId } }
) => {
  const tdo = state.tdos[tdoId];
  if (!tdo || tdo.status === 'recorded') {
    return state;
  }
  return {
    ...state,
    tdos: {
      ...state.tdos,
      [tdoId]: { ...tdo, status: 'recorded' as const },
    },
  };
};

// VE-23391: remember a TDO as newly added this session (any upload, not just the
// PDF fast path) so the grid flags its tile with a "New" pill.
const markOrgTdoNew: Re<{ tdoId: string }> = (state, { payload: { tdoId } }) =>
  state.newlyAddedTdoIds.includes(tdoId)
    ? state
    : { ...state, newlyAddedTdoIds: [...state.newlyAddedTdoIds, tdoId] };

// VE-23391: once the user has opened the file, drop its "New" pill (which also
// bounds the set).
const markOrgTdoSeen: Re<{ tdoId: string }> = (
  state,
  { payload: { tdoId } }
) =>
  state.newlyAddedTdoIds.includes(tdoId)
    ? {
        ...state,
        newlyAddedTdoIds: state.newlyAddedTdoIds.filter((id) => id !== tdoId),
      }
    : state;

// VE-24901: record the most recently opened TDO so the grid flags its tile with
// a "Last Opened" pill. Single id (flat org list); session-scoped.
const markOrgTdoLastOpened: Re<{ tdoId: string }> = (
  state,
  { payload: { tdoId } }
) =>
  state.lastOpenedTdoId === tdoId
    ? state
    : { ...state, lastOpenedTdoId: tdoId };

const changeFilter: Re<Pick<MainPageStore, 'filter'>> = (
  state,
  { payload: { filter } }
) => ({
  ...state,
  filter,
  currentPage: 1,
  tdos: {},
  fetchingOrganizationTdos: true,
  loadingNextPage: true,
  selectedTDOs: {},
});

const updatePagination: Re<Partial<MainPageStore>> = (state, { payload }) => ({
  ...state,
  ...payload,
});

const toggleSelectTDO: Re<{ tdoId: TDOId }> = (state, { payload }) => ({
  ...state,
  selectedTDOs: {
    ...state.selectedTDOs,
    [payload.tdoId]: !state.selectedTDOs[payload.tdoId],
  },
});

const toggleSelectAllTDOs: Re<{ filteredTDOs: SummaryTDO[] }> = (
  state,
  { payload }
) => {
  const { filteredTDOs } = payload;
  const selectedTDOCount = (
    Object.keys(state.selectedTDOs) as Array<keyof typeof state.selectedTDOs>
  ).filter((id) => state.selectedTDOs[id] === true).length;
  return {
    ...state,
    selectedTDOs:
      selectedTDOCount > 0
        ? {}
        : filteredTDOs.reduce((result: { [key: string]: boolean }, tdo) => {
            result[tdo.id] = true;
            return result;
          }, {}),
  };
};

const clearSelectedTDOs: Re = (state) => ({
  ...state,
  selectedTDOs: {},
});

const clearSelectedTDOsAndTagDeleting: Re = (state) => {
  const selectedTDOs = (
    Object.keys(state.selectedTDOs) as Array<keyof typeof state.selectedTDOs>
  ).filter((id) => state.selectedTDOs[id] === true);
  return {
    ...state,
    tdos: Object.keys(state.tdos).reduce<{ [key: string]: SummaryTDO }>(
      (result, tdoId) => {
        const tdos = state.tdos[tdoId];
        if (!tdos) {
          return result;
        }
        result[tdoId] = includes(selectedTDOs, tdoId)
          ? { ...tdos, isDeleting: true }
          : tdos;
        return result;
      },
      {}
    ),
    selectedTDOs: {},
  };
};

/**
 * MainPage composed reducers
 */
const reducers = createReducer(defaultState, (builder) => {
  builder
    .addCase(UPSERT_ORGANIZATION_SDO, upsertFetchAll)
    .addCase(FETCH_ORGANIZATION_SDOS, upsertFetchAll)
    .addCase(FETCH_ORGANIZATION_TDOS, upsertFetchAll)

    .addCase(
      UPSERT_ORGANIZATION_SDO_FAILURE,
      upsertFetchFailureAll(
        'Failed to update the media file. Please try again.'
      )
    )
    .addCase(
      FETCH_ORGANIZATION_SDOS_FAILURE,
      upsertFetchFailureAll(
        'Failed to preload the list of Media files. We will try again in a minute.'
      )
    )
    .addCase(
      FETCH_ORGANIZATION_TDOS_FAILURE,
      upsertFetchFailureAll(
        'Failed to load the list of media files. We will try again in a minute.'
      )
    )
    .addCase(UPDATE_STORE_SOFT_DELETED_TDOS, updateSoftDeletedtdos)
    .addCase(UPSERT_ORGANIZATION_SDO_SUCCESS, upsertOrgSDOSuccess)
    .addCase(FETCH_ORGANIZATION_SDOS_SUCCESS, fetchOrgSDOsSuccess)
    .addCase(FETCH_ORGANIZATION_TDOS_SUCCESS, fetchOrgTDOsSuccess)
    .addCase(FETCH_ORGANIZATION_TDO_BY_ID_SUCCESS, fetchOrgTDODetailSuccess)
    .addCase(DELETE_ORGANIZATION_SDO_SUCCESS_UPDATE_STORE, deleteOrgSDOSuccess)
    .addCase(UPDATE_ORG_TDOS_TASKS, onUpdateOrgTdosTasks)
    .addCase(MARK_ORG_TDO_RECORDED, markOrgTdoRecorded)
    .addCase(MARK_ORG_TDO_NEW, markOrgTdoNew)
    .addCase(MARK_ORG_TDO_SEEN, markOrgTdoSeen)
    .addCase(MARK_ORG_TDO_LAST_OPENED, markOrgTdoLastOpened)

    .addCase(CHANGE_FILTER, changeFilter)
    .addCase(UPDATE_PAGINATION, updatePagination)

    .addCase(TOGGLE_SELECT_TDO, toggleSelectTDO)
    .addCase(TOGGLE_SELECT_ALL_TDOS, toggleSelectAllTDOs)
    .addCase(CLEAR_SELECTED_TDOS, clearSelectedTDOs)
    .addCase(
      CLEAR_SELECTED_TDOS_AND_TAG_DELETING,
      clearSelectedTDOsAndTagDeleting
    )

    // update UI - TDO wrapper render upload fail
    .addCase(MARK_TDO_UPLOADING_END, (state, action) => {
      const { tdoId } = action.payload;
      return {
        ...state,
        listTDOsUploading: {
          ...state.listTDOsUploading,
          [tdoId]: false,
        },
      };
    })

    // mark tdo is running after create tdo success and createJob ingestion running
    .addCase(CREATE_TDO_SUCCESS, (state, action) => {
      const { id } = action.payload.createTDO;
      if (action.meta?.variables?.error) {
        return state;
      }
      return {
        ...state,
        listTDOsUploading: {
          ...state.listTDOsUploading,
          [id]: true,
        },
      };
    })
    .addCase(MARK_HOME_LIST_STALE, (state) => ({
      ...state,
      homeListStale: true,
    }))
    .addCase(CLEAR_HOME_LIST_STALE, (state) => ({
      ...state,
      homeListStale: false,
    }))
    .addCase(CLEAR_ALL, () => defaultState);
});

export default reducers;
